// src/components/Admin/PrivilegeCardModal.jsx
import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import { Download, X, Radio, Award, Loader2 } from 'lucide-react';
import './PrivilegeCardModal.css';

const API_BASE_URL = "https://api.lbnhyderabad.com" || 'https://api.lbnhyderabad.com';

export default function PrivilegeCardModal({ member, onClose }) {
  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!member) return null;

  const privCode = member.privilege_code || member.membership_number?.slice(-8) || 'PV9K4M2L';
  const supplierOffersUrl = `${window.location.origin}/offers?ref=${privCode}`;

  // Environment-safe resolver
  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    const cleanPath = url.startsWith('/') ? url : `/${url}`;
    return `${API_BASE_URL}${cleanPath}`;
  };

  const photoSrc = getPhotoUrl(member.profile_photo_url);

  // Sequential capture helper with cache-busting to prevent canvas CORS locks
  const captureCard = async (node, sideName) => {
    return toPng(node, {
      quality: 0.98,
      pixelRatio: 3,
      cacheBust: true,
    }).then((dataUrl) => {
      const sanitizedName = (member.full_name || 'Member').replace(/[^a-zA-Z0-9]/g, '_');
      const link = document.createElement('a');
      link.download = `PrivilegeCard_${sanitizedName}_${sideName}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handleDownloadBoth = async () => {
    if (!frontCardRef.current || !backCardRef.current || downloading) return;
    setDownloading(true);

    try {
      // 1. Download Front Side
      await captureCard(frontCardRef.current, 'FRONT');

      // 2. Buffer delay to prevent browser thread collision
      await new Promise((resolve) => setTimeout(resolve, 350));

      // 3. Download Back Side
      await captureCard(backCardRef.current, 'BACK');
    } catch (err) {
      console.error('Failed to capture card assets:', err);
      alert('Error exporting PNG cards. Ensure the photo URL is accessible.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="modal-backdrop-overlay">
      <div className="modal-glass-container">
        {/* Header Toolbar */}
        <div className="modal-header-toolbar">
          <div>
            <h3>Privilege Membership Card</h3>
            <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
              Standard ISO Format • Ready for Physical Production & Printing
            </span>
          </div>

          <div className="modal-header-actions">
            <button 
              className="btn-download-gold"
              onClick={handleDownloadBoth}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <Loader2 size={14} className="spin-loader" />
                  <span>Generating PNGs...</span>
                </>
              ) : (
                <>
                  <Download size={14} />
                  <span>Download Both Sides (PNG)</span>
                </>
              )}
            </button>
            <button className="btn-close-modal" onClick={onClose} disabled={downloading}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Dual Card Visual Stage */}
        <div className="card-stage-dual">
          {/* ================= CARD FRONT ================= */}
          <div>
            <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '0.75rem', fontWeight: 700, color: '#00529b' }}>
              CARD FRONT
            </div>
            <div className="privilege-card-frame" ref={frontCardRef}>
              {/* Top Banner */}
              <div className="card-front-top">
                <div className="card-brand-group">
                  <span className="brand-crest-line">Privilege Club</span>
                  <span className="brand-crest-tag">EXECUTIVE IDENTITY</span>
                </div>
                <div className="card-nfc-chip">
                  <Radio size={12} />
                  <span>PREMIUM ACCESS</span>
                </div>
              </div>

              {/* Middle Member Profile */}
              <div className="card-front-middle">
                <div className="card-member-photo-box">
                  {photoSrc ? (
                    <img 
                      src={photoSrc} 
                      alt={member.full_name} 
                      className="card-member-photo-img"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div 
                    className="card-member-avatar-placeholder" 
                    style={{ display: photoSrc ? 'none' : 'flex' }}
                  >
                    {member.full_name?.charAt(0) || 'M'}
                  </div>
                </div>

                <div className="card-member-details-col">
                  <div className="card-member-name">{member.full_name}</div>
                  <div className="card-membership-id">{member.membership_number}</div>
                  <div className="card-district-club">District {member.district} • {member.club_name}</div>
                  <div className="card-member-mobile">M: {member.mobile}</div>
                </div>
              </div>

              {/* Bottom Row */}
              <div className="card-front-bottom">
                <div className="card-privilege-code-box">
                  <span className="code-title">Privilege Identifier</span>
                  <span className="code-val">{privCode}</span>
                </div>

                <div className="card-manappuram-badge">
                  <Award size={18} color="#d97706" />
                  <div>
                    <span className="manappuram-text">MANAPPURAM</span>
                    <span className="manappuram-sub">FINANCE LIMITED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= CARD BACK ================= */}
          <div>
            <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '0.75rem', fontWeight: 700, color: '#00529b' }}>
              CARD BACK (Offers QR + Terms)
            </div>
            <div className="privilege-card-frame card-back-frame" ref={backCardRef}>
              <div className="card-back-header">
                Privilege Partner Network
              </div>

              <div className="card-back-body">
                <div className="card-back-qr-box">
                  <QRCodeSVG 
                    value={supplierOffersUrl}
                    size={76}
                    bgColor="#ffffff"
                    fgColor="#002244"
                    level="M"
                  />
                </div>

                <div className="card-back-instructions">
                  <h4>Scan for Exclusive Deals</h4>
                  <p>
                    Point camera or tap NFC to access accredited suppliers, commercial discounts, and partner catalog offers.
                  </p>
                  <p style={{ color: '#febf2c', fontWeight: 800, marginTop: '4px' }}>
                    Code: {privCode}
                  </p>
                </div>
              </div>

              <div className="card-back-footer">
                Non-transferable • Issued by Privilege Card Authority
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}